import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';
import { useToast } from '@/hooks/errors/use-toast';

export interface PrivatePage {
  id: string;
  title: string;
  content: string;
  type: string | null;
  writing_style: string | null;
  initial_request: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  workbook_id: string | null; // Can be null (private) or assigned to projects
  openai_thread_id: string | null;
}

/**
 * Hook for fetching user pages with optional project filtering
 * When projectId is provided, shows only pages from that project
 * When projectId is null/undefined, shows ALL pages the user has created
 */
export const usePrivatePages = (projectId?: string | null) => {
  const { user } = useUser();

  return useQuery({
    queryKey: ['private-pages', user?.id, projectId],
    queryFn: async (): Promise<PrivatePage[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('[usePrivatePages] Fetching user pages for user:', user.id, 'projectId:', projectId);

      let query = supabase
        .from('workbook_page_content')
        .select('*')
        .eq('user_id', user.id);

      // Add project filter if provided
      if (projectId !== undefined && projectId !== null) {
        // Filter for pages assigned to specific project
        query = query.eq('workbook_id', projectId);
        console.log('[usePrivatePages] Filtering by project:', projectId);
      }
      // If projectId is null or undefined, show all user pages (no filtering)

      const { data, error } = await query.order('updated_at', { ascending: false });

      if (error) {
        console.error('[usePrivatePages] Error fetching user pages:', error);
        throw error;
      }

      console.log('[usePrivatePages] Successfully fetched', data?.length || 0, 'user pages for project:', projectId || 'all');
      return data as PrivatePage[];
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds - prevent constant refetching
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: true, // Only refetch when component mounts
  });
};

/**
 * Hook for creating a new private page
 */
export const useCreatePrivatePage = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pageData: {
      title: string;
      content?: string;
      type?: string;
      writing_style?: string;
      initial_request?: string;
    }): Promise<PrivatePage> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('[useCreatePrivatePage] Creating new private page:', pageData.title);

      const { data, error } = await supabase
        .from('workbook_page_content')
        .insert({
          title: pageData.title,
          content: pageData.content || '',
          type: pageData.type || 'Standard Page',
          writing_style: pageData.writing_style,
          initial_request: pageData.initial_request || '',
          user_id: user.id,
          workbook_id: null, // NULL = private page
          openai_thread_id: null, // No thread for manual creation
        })
        .select()
        .single();

      if (error) {
        console.error('[useCreatePrivatePage] Error creating private page:', error);
        throw error;
      }

      console.log('[useCreatePrivatePage] Successfully created private page:', data.id);
      return data as PrivatePage;
    },
    onSuccess: (newPage) => {
      // Invalidate private pages query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['private-pages', user?.id] });
      
      toast({
        title: "Success",
        description: "Private page created successfully",
      });
    },
    onError: (error) => {
      console.error('[useCreatePrivatePage] Error creating private page:', error);
      toast({
        title: "Error",
        description: "Failed to create private page",
        variant: "destructive",
      });
    },
  });
};

/**
 * Hook for deleting a user page (both private and project pages)
 */
export const useDeletePrivatePage = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pageId: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('[useDeletePrivatePage] Deleting user page:', pageId);

      const { error } = await supabase
        .from('workbook_page_content')
        .delete()
        .eq('id', pageId)
        .eq('user_id', user.id); // Security: ensure user owns the page
        // REMOVED: .is('workbook_id', null) - now allows deleting any user page

      if (error) {
        console.error('[useDeletePrivatePage] Error deleting user page:', error);
        throw error;
      }

      console.log('[useDeletePrivatePage] Successfully deleted user page:', pageId);
    },
    onSuccess: () => {
      // Invalidate private pages query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['private-pages', user?.id] });
      
      toast({
        title: "Success",
        description: "Page deleted successfully",
      });
    },
    onError: (error) => {
      console.error('[useDeletePrivatePage] Error deleting user page:', error);
      toast({
        title: "Error",
        description: "Failed to delete page",
        variant: "destructive",
      });
    },
  });
}; 